import HASHTAG_SCAN_MAP from '../constants/hashtagScanMap';
import Logger from './logger';

/**
 * STORY-008 Hashtag Filter Engine
 *
 * Pure functions that:
 *  1. Evaluate a threshold `condition` against a single symbol's scan entry.
 *  2. Build the matching symbol set for a condition against cached scan data.
 *  3. Pool all conditions of the selected hashtags, intersect the matching
 *     symbol sets, and return the resulting symbol IDs sorted by MarketCap
 *     descending.
 *
 * Design mirrors the desktop scanResultController pattern of chained
 * narrowing filters. See STORY-008 spec §Implementation Guidance.
 *
 * IMPORTANT:
 *  - Thresholds live in `src/constants/hashtagFilterConfig.js` and are
 *    stored in the SAME units as the Syphon `filterValue` — no unit math
 *    is performed here.
 *  - No LLM involvement (NFR-6).
 */

/** Reads `filterValue` off a symbol entry, tolerating either shape. */
function readFilterValue(symbolEntry) {
  if (!symbolEntry || typeof symbolEntry !== 'object') return undefined;
  return symbolEntry.filterValue;
}

/** Coerces a Set / iterable / nullish selection into a Set (never throws). */
function ensureSet(value) {
  return value instanceof Set ? value : new Set(value || []);
}

/**
 * Extracts `CrossoverDaysAgo` for the `crossoverWithinDays` operator.
 *
 * Syphon's `SMACrossover_5_25` scan is declared with
 *   SourceField: 'CrossoverDate,CrossoverClose,CrossoverDaysAgo', ValueIndex: 2
 * in ScannerCategory.js, so `filterValue` is the plain `CrossoverDaysAgo`
 * NUMBER. We also tolerate legacy/alternate shapes seen historically:
 * `filterValue` as a `{ CrossoverDaysAgo }` object, a root-level
 * `CrossoverDaysAgo`, and `displayValues.{CrossoverDaysAgo|crossoverDaysAgo}`.
 *
 * IMPORTANT: read each candidate explicitly to a nullable value before coercing
 * to Number. `A && A.field` short-circuits to `false` when A is a truthy
 * non-object (e.g. the number 5), and `Number(false) === 0` — a finite value
 * that would incorrectly match "within N days" for every symbol. Each fallback
 * only applies while the running value is still non-finite, so a legitimate `0`
 * (crossover today) stops the chain rather than falling through.
 *
 * @param {object} symbolEntry
 * @returns {number} the days-ago value, or NaN when unavailable
 */
function extractCrossoverDaysAgo(symbolEntry) {
  const pickNumber = (raw) => {
    if (raw === null || raw === undefined || raw === '') return NaN;
    const n = Number(raw);
    return Number.isFinite(n) ? n : NaN;
  };

  const fv = readFilterValue(symbolEntry);
  const dv = symbolEntry && symbolEntry.displayValues;

  let daysAgo = NaN;
  if (typeof fv === 'number') {
    daysAgo = fv;
  } else if (typeof fv === 'string') {
    daysAgo = pickNumber(fv);
  } else if (fv && typeof fv === 'object') {
    daysAgo = pickNumber(fv.CrossoverDaysAgo);
  }
  if (!Number.isFinite(daysAgo) && symbolEntry) {
    daysAgo = pickNumber(symbolEntry.CrossoverDaysAgo);
  }
  if (!Number.isFinite(daysAgo) && dv) {
    daysAgo = pickNumber(dv.CrossoverDaysAgo);
  }
  if (!Number.isFinite(daysAgo) && dv) {
    daysAgo = pickNumber(dv.crossoverDaysAgo);
  }
  return daysAgo;
}

/**
 * Evaluate a single condition against one symbol's scan entry.
 * Pure. Returns `false` on any missing/malformed input.
 *
 * @param {object} symbolEntry - `{ filterValue, displayValues }` from
 *   `state.scans.scanData[scanKey].symbols[symbolId]`.
 * @param {{scanName:string, operator:string, value:*}} condition
 * @returns {boolean}
 */
export function evaluateCondition(symbolEntry, condition) {
  if (!condition) return false;
  const fv = readFilterValue(symbolEntry);
  const { operator, value } = condition;

  switch (operator) {
    case '>=':
    case '<=':
    case '>':
    case '<':
    case '=': {
      const n = typeof fv === 'number' ? fv : Number(fv);
      if (!Number.isFinite(n)) return false;
      if (operator === '>=') return n >= value;
      if (operator === '<=') return n <= value;
      if (operator === '>') return n > value;
      if (operator === '<') return n < value;
      return n === value;
    }
    case 'inCodeSet': {
      if (fv === undefined || fv === null) return false;
      if (!Array.isArray(value)) return false;
      return value.indexOf(String(fv)) !== -1;
    }
    case 'notInCodeSet': {
      // Symbol matches when its filterValue is NOT in the excluded code set.
      // Used e.g. by #4 SecurityType to select common stock by excluding
      // ETF/REIT SectionClassCodes ('0115','0116','0119').
      if (fv === undefined || fv === null) return false;
      if (!Array.isArray(value)) return false;
      return value.indexOf(String(fv)) === -1;
    }
    case 'stringEquals': {
      if (typeof fv !== 'string') return false;
      return fv === value;
    }
    case 'stringContains': {
      if (typeof fv !== 'string' || typeof value !== 'string') return false;
      return fv.indexOf(value) !== -1;
    }
    case 'crossoverWithinDays': {
      // `filterValue` shape/fallback handling lives in extractCrossoverDaysAgo.
      const daysAgo = extractCrossoverDaysAgo(symbolEntry);
      if (!Number.isFinite(daysAgo)) return false;
      return daysAgo <= value;
    }
    default:
      // Unknown operator — treat as no match. Config schema tests guard the
      // config itself; a caller boundary logs E7101 if this ever fires.
      return false;
  }
}

/**
 * Build the set of symbol IDs that satisfy the condition against cached
 * scan data. Returns an empty Set when the required scan is not cached.
 *
 * @param {object} scanData - `state.scans.scanData`
 * @param {{scanName:string, operator:string, value:*}} condition
 * @returns {Set<string>}
 */
export function matchSymbolsForCondition(scanData, condition) {
  const matched = new Set();
  if (!condition || !scanData) return matched;
  const key = String(condition.scanName || '').toLowerCase();
  const entry = scanData[key];
  const symbols = entry && entry.symbols;
  if (!symbols) return matched;

  Object.keys(symbols).forEach((symbolId) => {
    if (evaluateCondition(symbols[symbolId], condition)) {
      matched.add(symbolId);
    }
  });
  return matched;
}

/**
 * The union of all scan names required by the selected hashtags,
 * deduplicated. Parity with `getScansForSelection` in hashtagScanMap.js.
 *
 * @param {Iterable<string>} selectedIds
 * @param {object} config - HASHTAG_FILTER_CONFIG
 * @returns {string[]}
 */
export function getRequiredScans(selectedIds, config) {
  const out = new Set();
  const ids = ensureSet(selectedIds);
  ids.forEach((id) => {
    const conditions = config && config[id];
    if (!conditions) return;
    conditions.forEach((c) => out.add(c.scanName));
  });
  return Array.from(out);
}

/**
 * Sort a symbol list by MarketCap descending. Symbols missing MarketCap
 * data are placed after those with data. Stable.
 *
 * @param {string[]} symbols
 * @param {object} scanData
 * @returns {string[]}
 */
function sortByMarketCapDesc(symbols, scanData) {
  const marketCapSymbols =
    (scanData && scanData.marketcap && scanData.marketcap.symbols) || null;

  const getCap = (symbolId) => {
    if (!marketCapSymbols) return null;
    const entry = marketCapSymbols[symbolId];
    if (!entry) return null;
    const n = Number(entry.filterValue);
    return Number.isFinite(n) ? n : null;
  };

  return symbols
    .map((s, idx) => ({ s, idx, cap: getCap(s) }))
    .sort((a, b) => {
      if (a.cap === b.cap) return a.idx - b.idx;
      if (a.cap === null) return 1;
      if (b.cap === null) return -1;
      return b.cap - a.cap;
    })
    .map(({ s }) => s);
}

/**
 * Intersection of two symbol sets. Iterates the smaller set and probes the
 * larger, so cost is O(min(|a|,|b|)) rather than O(|a|).
 *
 * @param {Set<string>} a
 * @param {Set<string>} b
 * @returns {Set<string>} a new Set (inputs are not mutated)
 */
function intersectSets(a, b) {
  const [small, big] = a.size <= b.size ? [a, b] : [b, a];
  const next = new Set();
  small.forEach((s) => {
    if (big.has(s)) next.add(s);
  });
  return next;
}

/**
 * Compute the intersection of a single hashtag's own conditions (AND
 * within a hashtag: every condition must pass).
 *
 * @param {object} scanData
 * @param {ReadonlyArray<{scanName:string, operator:string, value:*}>} conditions
 * @returns {Set<string>}
 */
function matchSymbolsForHashtag(scanData, conditions) {
  let intersection = null;
  for (let i = 0; i < conditions.length; i += 1) {
    const set = matchSymbolsForCondition(scanData, conditions[i]);
    intersection =
      intersection === null ? set : intersectSets(intersection, set);
    if (intersection.size === 0) break;
  }
  return intersection || new Set();
}

/**
 * For each selected hashtag, compute the intersection of that hashtag's
 * conditions (AND within a hashtag). Then INTERSECT the per-hashtag matching
 * sets across all selected hashtags — a stock is kept only if it satisfies
 * EVERY selected hashtag's full condition set. Equivalent to pooling all
 * selected hashtags' conditions and applying them together (spec §Combination
 * model: "union of the filter conditions" → intersection of matching symbols).
 *
 * Order-independent w.r.t. `selectedIds`. Adding a hashtag can only keep the
 * count the same or narrow it (never grow it).
 *
 * @param {object} scanData - `state.scans.scanData`
 * @param {Iterable<string>} selectedIds
 * @param {object} config - HASHTAG_FILTER_CONFIG
 * @returns {string[]} matching symbol IDs, sorted MarketCap desc
 */
export function filterSelectedHashtags(scanData, selectedIds, config) {
  const ids = ensureSet(selectedIds);
  if (ids.size === 0) return [];
  if (!config) return [];

  try {
    const idList = Array.from(ids);
    let intersection = null;
    for (let i = 0; i < idList.length; i += 1) {
      const conds = config[idList[i]];
      // A selected id with no configured conditions adds no constraint.
      if (!conds || conds.length === 0) continue;

      const perHashtag = matchSymbolsForHashtag(scanData, conds);
      intersection =
        intersection === null
          ? perHashtag
          : intersectSets(intersection, perHashtag);
      if (intersection.size === 0) break;
    }

    return sortByMarketCapDesc(Array.from(intersection || new Set()), scanData);
  } catch (error) {
    Logger.error({
      errorCode: 'E7101',
      message: 'SCAN_FILTER_EVALUATION_FAILED',
      error
    });
    return [];
  }
}

// Re-export as a convenience so callers importing the engine can look up
// the fired-scan list without a separate import.
export { HASHTAG_SCAN_MAP };
